/**
 * 67.75 steps 1c–1d: the frame family — the steel cages vehicles and equipment ship in, from orbit to the base and between bases
 * (the owner, Oct 19: *"We'll also probably need to figure out where all these vehicles and dropships come from.  Was thinking
 * for things like APCs and Technicals they'd come in metal frames to make a box around them, maybe with solid walls maybe not,
 * then they could be stacked around the base when not in use or deployed.  Then we could also easily show the logistics from
 * orbital ships to surface, explaining where vehicles come from."*; on 1c: *"this also introduces the idea of equipment
 * containers … Also theoretically the dropships would be shipped as well so could they fit in a double wide container?"*).
 *
 * One footprint for the family (`FRAME`, rendering/orbitShips.ts: 7.6 × 3.5 × 4.0 m outside, 7.2 × 3.1 × 3.5 clear above the
 * deck, round the BTR's length and the M113's height), with corner castings at all eight corners so frames stack on each other
 * and lock to a lighter's clamps or a handler's spreader (rendering/motorPool.ts), forklift pockets, a deck with tie-down rails.
 * The variants:
 *   - **open** (a vehicle's): posts, rails and X-braced sides, ends open to drive through, roof bars, a numbered placard; a
 *     ramp at the front end that folds down to the ground to unpack (`ramp`, 0 up as a gate to 1 down);
 *   - **walled**: corrugated walls and roof in the company's colour, the name along the sides, a closed rear end and two door
 *     leaves at the front (`doors`, 0 shut to 1 standing open, ~100°);
 *   - **equipment containers** (1d), walled with their markings: `ammo` (a hazard band, an explosive diamond, AMMO), `supply`
 *     (a cream band, SUPPLY), `medical` (cream walls, a cross in the company's colour, MED), `fab` (a fabrication module: one
 *     side open under a raised awning, the machinery and a fabricator bed inside, vents and a stack on the roof, FAB), `fuel`
 *     (an open frame holding a rubber bladder under straps, a valve, a yellow FUEL placard);
 *   - **bigger frames** (`span` 2 across, `long` 2 along, open only): the double-wide (7.6 × 7.0 × 4.0) and the quad (15.2 ×
 *     7.0 × 4.0) — the quad takes a dropship with its gear up and its four nacelles off (racked beside it); the double-wide
 *     doesn't (the ship is 9.8 m long).
 * Its origin is the middle of its floor on the ground; +z its front (doors, ramp), as a vehicle in it faces; what it carries
 * stands on the deck at `FRAME_DECK`. Built from the ships' kit (one spec, merged, the paint per vertex) and drawn with a Lambert
 * material like the ground's props: one draw a frame. Presentation only: no frame is in the sim yet (vehicles stored as frames,
 * unpacked to deploy, is a later row). The sheets are design_sheets/vehicle_frames.html and motor_pool.html.
 */
import * as THREE from 'three';
import { Spec, mergedTier, FRAME, type Livery, type Paint } from './orbitShips';
import type { UnitPalette } from './unitPalette';

export type FrameVariant = 'open' | 'walled' | 'ammo' | 'supply' | 'fab' | 'fuel' | 'medical';
export const EQUIPMENT: readonly FrameVariant[] = ['ammo', 'supply', 'fab', 'fuel', 'medical'];
/** The deck's top over the ground (metres): where a vehicle's wheels or tracks stand. */
export const FRAME_DECK = 0.31;
export interface FrameOptions {
  variant: FrameVariant;
  number: number;
  /** a walled frame's doors: 0 shut, 1 standing open */
  doors?: number;
  /** an open frame's front ramp: 0 up (a gate), 1 down on the ground */
  ramp?: number;
  /** frames across (1 or 2) and along (1 or 2): open frames only */
  span?: 1 | 2;
  long?: 1 | 2;
}
const E = 0.22, C = 0.32;

/** The cage every variant shares: castings, posts, rails, pockets, the deck and its tie-downs. */
function cage(s: Spec, W: number, L: number, span: number, long: number): void {
  const H = FRAME.height, xs = span === 2 ? [-1, 0, 1] : [-1, 1], zs = long === 2 ? [-1, 0, 1] : [-1, 1];
  // castings: every frame's corners, so a joined frame still stacks on (or under) single ones
  for (const ix of xs) for (const iz of zs) for (const y of [C / 2, H - C / 2]) {
    const xx = ix === 0 ? [-C / 2, C / 2] : [ix * (W / 2 - C / 2)], zz = iz === 0 ? [-C / 2, C / 2] : [iz * (L / 2 - C / 2)];
    for (const x of xx) for (const z of zz) s.box(C, C, C, x, y, z, 'machLt');
  }
  for (const ix of xs) for (const iz of zs) if (ix !== 0 || iz !== 0) {
    // (posts round the outside; none in the middle of a quad)
    if (ix !== 0 && iz !== 0) s.box(E, H - 2 * C, E, ix * (W / 2 - E / 2), H / 2, iz * (L / 2 - E / 2), 'team');
    else if (ix === 0) s.box(E, H - 2 * C, E, 0, H / 2, iz * (L / 2 - E / 2), 'team');
    else s.box(E, H - 2 * C, E, ix * (W / 2 - E / 2), H / 2, 0, 'team');
  }
  for (const sx of [-1, 1]) {
    s.box(E, 0.34, L - 2 * C, sx * (W / 2 - E / 2), 0.17, 0, 'team');
    s.box(E, E, L - 2 * C, sx * (W / 2 - E / 2), H - E / 2, 0, 'team');
    for (let k = 0; k < long; k++) for (const z of [-1.6, 1.6]) s.box(0.02, 0.2, 0.9, sx * (W / 2 + 0.005), 0.17, z + (k - (long - 1) / 2) * FRAME.length, 'bay');
  }
  for (const sz of [-1, 1]) {
    s.box(W - 2 * C, 0.34, E, 0, 0.17, sz * (L / 2 - E / 2), 'team');
    s.box(W - 2 * C, E, E, 0, H - E / 2, sz * (L / 2 - E / 2), 'team');
  }
  s.box(W - 2 * E, 0.1, L - 2 * E, 0, FRAME_DECK - 0.05, 0, 'machDk');
  const rails = span === 2 ? [-2.6, -0.9, 0.9, 2.6] : [-0.85, 0.85];
  for (const x of rails) s.box(0.14, 0.05, L - 0.8, x, FRAME_DECK + 0.02, 0, 'metal');
}

/** The open sides: mid posts, X braces bay by bay, roof bars. */
function openSides(s: Spec, W: number, L: number, long: number): void {
  const H = FRAME.height, bays = 2 * long, bl = (L - 2 * C) / bays - 0.08, bh = H - 2 * C - 0.1, diag = Math.hypot(bl, bh), a = Math.atan2(bh, bl);
  for (const sx of [-1, 1]) {
    for (let b = 1; b < bays; b++) if (!(long === 2 && b === 2)) s.box(0.16, H - 2 * C, 0.16, sx * (W / 2 - E / 2), H / 2, -L / 2 + C + (b * (L - 2 * C)) / bays, 'team');
    for (let b = 0; b < bays; b++) for (const r of [-a, a]) s.box(0.1, 0.1, diag, sx * (W / 2 - E / 2), H / 2, -L / 2 + C + ((b + 0.5) * (L - 2 * C)) / bays, 'teamDk', { rx: r });
  }
  const bars = Math.max(2, Math.round(L / 1.9) - 1);
  for (let k = 1; k <= bars; k++) s.box(W - 2 * E, 0.14, 0.14, 0, H - E / 2, -L / 2 + (k * L) / (bars + 1), 'team');
}

/** A placard on each side's middle post: a plate (cream, or the paint given) with one or two lines of stencil. */
function placard(s: Spec, W: number, lines: [string, string], plate: Paint = 'cream'): void {
  for (const sx of [-1, 1]) s.box(0.05, 1.1, 1.0, sx * (W / 2 - 0.06), 1.9, 0, plate);
  s.text(lines[0], W / 2 - 0.035, 2.2, 0, 0.3, 0.8, 'team');
  s.text(lines[1], W / 2 - 0.035, 1.72, 0, 0.5, 0.8, 'team');
}

/** The walled body: walls on `sides` (both, or one), ribbed; roof; the rear closed; the front's two door leaves. */
function walls(s: Spec, W: number, L: number, wall: Paint, rib: Paint, band: Paint, doors: number, sides: (1 | -1)[] = [-1, 1]): void {
  const H = FRAME.height;
  for (const sx of sides) {
    s.box(0.05, H - 2 * C + 0.1, L - 2 * C, sx * (W / 2 - 0.09), H / 2, 0, wall);
    for (let z = -L / 2 + C + 0.2; z < L / 2 - C; z += 0.38) s.box(0.07, H - 2 * C, 0.12, sx * (W / 2 - 0.035), H / 2, z, rib);
    s.box(0.08, 0.28, L - 2 * C, sx * (W / 2 - 0.02), H - 0.62, 0, band);
  }
  s.box(W - 2 * E + 0.1, 0.05, L - 2 * C, 0, H - 0.1, 0, wall);
  s.box(W - 2 * C, H - 2 * C, 0.05, 0, H / 2, -(L / 2 - 0.08), wall);
  for (let x = -W / 2 + C + 0.2; x < W / 2 - C; x += 0.38) s.box(0.12, H - 2 * C, 0.07, x, H / 2, -(L / 2 - 0.035), rib);
  const lw = (W - 2 * E) / 2, hl = H - 2 * C - 0.05, phi = doors * Math.PI * 0.56, zh = L / 2 - 0.06;
  for (const sx of [-1, 1]) {
    const hx = sx * (W / 2 - E), v = new THREE.Vector3(-sx * Math.cos(phi), 0, Math.sin(phi)), n = new THREE.Vector3(sx * Math.sin(phi), 0, Math.cos(phi));
    const mid = new THREE.Vector3(hx, H / 2, zh).addScaledVector(v, lw / 2), ry = sx * phi;
    s.box(lw, hl, 0.06, mid.x, mid.y, mid.z, wall, { ry });
    for (const t of [0.3, 0.75]) { const r = new THREE.Vector3(hx, H / 2, zh).addScaledVector(v, lw * t).addScaledVector(n, 0.06); s.box(0.06, hl - 0.1, 0.06, r.x, r.y, r.z, 'machLt', { ry }); }
  }
}

function frame(s: Spec, o: FrameOptions): void {
  const span = o.variant === 'open' ? (o.span ?? 1) : 1, long = o.variant === 'open' ? (o.long ?? 1) : 1;
  const L = FRAME.length * long, W = FRAME.width * span, H = FRAME.height, no = String(o.number).padStart(2, '0'), face = W / 2 + 0.005;
  cage(s, W, L, span, long);
  switch (o.variant) {
    case 'open': {
      openSides(s, W, L, long);
      placard(s, W, [span * long > 1 ? 'VF-X' : 'VF', no]);
      // the front ramp: a gate standing up, folding down to the ground to unpack
      const th = Math.PI / 2 - (o.ramp ?? 0) * (Math.PI / 2 + Math.asin(FRAME_DECK / 1.6)), len = 1.6;
      const dir = new THREE.Vector3(0, Math.sin(th), Math.cos(th)), mid = new THREE.Vector3(0, FRAME_DECK, L / 2 - E).addScaledVector(dir, len / 2);
      s.box(W - 2 * E - 0.2, 0.08, len, mid.x, mid.y, mid.z, 'teamDk', { rx: -th });
      for (const x of [-0.85, 0.85]) s.box(0.3, 0.1, len, x * Math.min(1, span), mid.y + Math.cos(th) * 0.05, mid.z - Math.sin(th) * 0.05, 'metal', { rx: -th });
      break;
    }
    case 'walled':
      walls(s, W, L, 'team', 'teamDk', 'teamLt', o.doors ?? 0);
      s.name(face, H * 0.52, 0.3, 0.6, L - 2.2, 'cream');
      s.text(`VF ${no}`, face, H * 0.24, -L / 2 + 1.5, 0.32, 1.8, 'cream');
      break;
    case 'ammo': {
      walls(s, W, L, 'team', 'teamDk', 'hazard', o.doors ?? 0);
      for (const sx of [-1, 1]) {
        s.box(0.08, 0.5, L - 2 * C, sx * (W / 2 - 0.015), H - 0.95, 0, 'hazard');
        for (let z = -L / 2 + C + 0.3; z < L / 2 - C - 0.2; z += 0.6) s.box(0.09, 0.62, 0.16, sx * (W / 2 - 0.01), H - 0.95, z, 'machDk', { rx: 0.7 });
        s.box(0.08, 0.8, 0.8, sx * (W / 2 - 0.01), H * 0.47, 2.5, 'hazard', { rx: Math.PI / 4 });
        s.box(0.09, 0.32, 0.32, sx * (W / 2 - 0.005), H * 0.47, 2.5, 'machDk', { rx: Math.PI / 4 });
      }
      s.text('AMMO', face + 0.02, H * 0.47, -0.6, 0.95, 3.2, 'cream');
      s.name(face + 0.02, 0.85, -0.6, 0.3, 3, 'cream');
      s.text(`EQ ${no}`, face + 0.02, H * 0.24, -L / 2 + 1.0, 0.3, 1.2, 'cream');
      break;
    }
    case 'supply':
      walls(s, W, L, 'team', 'teamDk', 'cream', o.doors ?? 0);
      for (const sx of [-1, 1]) s.box(0.08, 0.55, L - 2 * C, sx * (W / 2 - 0.015), H - 0.95, 0, 'cream');
      s.text('SUPPLY', face + 0.02, H * 0.47, 0, 0.95, 5, 'cream');
      s.name(face + 0.02, 0.85, 0, 0.3, 3, 'cream');
      s.text(`EQ ${no}`, face + 0.02, H * 0.24, -L / 2 + 1.0, 0.3, 1.2, 'cream');
      break;
    case 'medical':
      walls(s, W, L, 'cream', 'cream', 'teamLt', o.doors ?? 0);
      for (const sx of [-1, 1]) {
        s.box(0.08, 1.5, 0.48, sx * (W / 2 - 0.01), H * 0.5, 2.0, 'team');
        s.box(0.08, 0.48, 1.5, sx * (W / 2 - 0.01), H * 0.5, 2.0, 'team');
      }
      s.text('MED', face + 0.02, H * 0.5, -1.0, 0.95, 2.6, 'team');
      s.name(face + 0.02, 0.85, -0.6, 0.3, 3, 'team');
      s.text(`EQ ${no}`, face + 0.02, H * 0.24, -L / 2 + 1.0, 0.3, 1.2, 'team');
      break;
    case 'fab': {
      // one side walled (−x) with the marks; the other open under its awning, the machinery inside
      walls(s, W, L, 'team', 'teamDk', 'amber', o.doors ?? 0, [-1]);
      s.box(0.08, 0.5, L - 2 * C, -(W / 2 - 0.015), H - 0.95, 0, 'amber');
      s.text('FAB', face + 0.02, H * 0.47, -0.8, 0.95, 2.4, 'cream', 0, -1);
      s.name(face + 0.02, 0.85, -0.6, 0.3, 3, 'cream', -1);
      s.box(2.2, 0.08, L - 2 * C - 0.4, W / 2 + 1.05, H - 0.4, 0, 'team');
      s.box(2.22, 0.1, 0.4, W / 2 + 1.05, H - 0.34, 0, 'amber');
      for (const z of [-2.8, 2.8]) s.box(0.06, 0.06, 2.4, W / 2 + 1.0, H - 1.1, z, 'machLt', { rx: 0, rz: 0.6, ry: Math.PI / 2 });
      s.box(1.3, 1.0, 4.6, 0.4, FRAME_DECK + 0.5, -0.6, 'machDk');
      s.box(1.1, 0.12, 3.4, 0.4, FRAME_DECK + 1.06, -0.6, 'cream');
      s.box(0.3, 1.6, 0.3, 0.2, FRAME_DECK + 1.9, -2.0, 'amber'); s.box(0.22, 0.22, 1.4, 0.2, FRAME_DECK + 2.6, -1.4, 'amber', { rx: 0.3 });
      for (const [x, z, w, h, d] of [[-0.9, 2.4, 0.9, 2.4, 1.3], [-0.95, -2.9, 0.8, 2.0, 1.1], [0.6, 2.6, 1.2, 1.2, 1.0]] as const) s.box(w, h, d, x, FRAME_DECK + h / 2, z, 'mach');
      for (const z of [-1.5, 0.5]) s.box(0.9, 0.35, 0.9, -0.6, H + 0.15, z, 'machDk');
      s.cyl(0.25, 1.6, 'y', 0.8, H + 0.8, 2.2, 'machDk', { seg: 8 });
      s.text(`EQ ${no}`, face + 0.02, H * 0.24, -L / 2 + 1.0, 0.3, 1.2, 'cream', 0, -1);
      break;
    }
    case 'fuel': {
      openSides(s, W, L, 1);
      placard(s, W, ['FUEL', no], 'caution');
      const r = 1.3, y = FRAME_DECK + r;
      s.cyl(r, 4.3, 'z', 0, y, 0, 'rubber', { seg: 14 });
      for (const sz of [-1, 1]) s.sphere(r, 0, y, sz * 2.15, 'rubber');
      for (const z of [-1.6, 0, 1.6]) s.torus(r + 0.04, 0.07, 'z', 0, y, z, 'caution');
      s.cyl(0.18, 0.5, 'y', 0.6, y + r + 0.1, 2.0, 'machLt', { seg: 8 }); s.cyl(0.32, 0.1, 'y', 0.6, y + r + 0.35, 2.0, 'hazard', { seg: 10 });
      break;
    }
  }
}

/** One frame: its mesh (one draw, a Lambert material reading the paint per vertex) and its triangles. */
export function buildVehicleFrame(p: UnitPalette, livery: Livery, o: FrameOptions): { mesh: THREE.Mesh; triangles: number } {
  const spec = new Spec(livery);
  frame(spec, o);
  const g = mergedTier(spec, p, false);
  g.setAttribute('color', g.getAttribute('paint'));
  g.deleteAttribute('glow');
  const mesh = new THREE.Mesh(g, new THREE.MeshLambertMaterial({ vertexColors: true }));
  return { mesh, triangles: g.attributes.position.count / 3 };
}
